import { FC, useEffect, useLayoutEffect, useState } from "react";
import { Alert, FormControl, FormLabel, Grid, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { Select } from "../../createBadge/form/components";
import { useSelector } from "react-redux";
import { productsSelector } from "../../../redux/magento/selectors/magento.selectors";
import productsUtils from "../../../common/utils/productsUtils";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";
import colors from "../../../common/styles/colors";
import { useFreyaSetup } from "../../freya/hooks/useFreyaSetup";
import { OrientationToggle } from "../orientation/OrientationToggle";
import { FREYA_ORIENTATION_KEY } from "../../../common/constants";
import { FreyaPrintSettingsDeviceSelector } from "./FreyaPrintSettingsDeviceSelector";
import { useCameraScanner } from "./useCameraScanner";
import { ScannerCheckbox } from "./scannerCheckbox/ScannerCheckbox";

export const FREYA_DEVICE_ID_KEY = "DEVICE_ID";

const useStyles = makeStyles({
  container: {
    marginTop: 40,
    justifyContent: "space-between",
    display: "flex",
    flexDirection: "column"
  },
  select: {
    width: "100%"
  },
  reloadIcon: {
    transform: "scaleX(-1) rotate(-45deg)",
    color: colors.primary,
    cursor: "pointer"
  },
  inputLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: colors.darkGrey1,
    marginBottom: 7
  },
  titleIcon: {
    marginRight: 10
  },
  title: {
    marginBottom: 15,
    fontWeight: 400,
    fontSize: 20,
    color: colors.darkGrey1,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center"
  },
  subtitle: {
    marginBottom: 22,
    fontWeight: 400,
    fontSize: 14,
    color: colors.lightGrey10
  },
  alert: {
    marginBottom: 32,
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: -0.441,
    color: colors.brownColor
  },
  inputsTitle: {
    color: colors.greenColor1,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase"
  },
  selectGrid: {
    marginBottom: 0,
    marginTop: -12
  },
  selectTest: {
    top: "367px !important",
    transform: "rotate(180deg) !important"
  }
});

interface Props {
  setSaveDisabled: (state: boolean) => void;
}

export const FreyaPrintSettings: FC<Props> = ({ setSaveDisabled }) => {
  const [selectedZone, setSelectedZone] = useState<string>("");
  const classes = useStyles();
  const products = useSelector(productsSelector);
  const zoneLocations = productsUtils.getValidZones(products);
  const { t } = useTranslation("common");

  const { cameraDevices, selectedCameraDevice, initCameraDevices, setDeviceIdToIndexDB, setCamera } = useCameraScanner({
    useIndexDB: true
  });
  const { checkProducts, zone } = useFreyaSetup();

  const preselectZone = async () => {
    const cachedZoneId = await zone.get();
    if (cachedZoneId && zoneLocations.find(zoneLocation => zoneLocation.id === +cachedZoneId)) {
      setSelectedZone(cachedZoneId);
      return;
    }
    if (!selectedZone && zoneLocations[0]) {
      setSelectedZone(zoneLocations[0].id as unknown as string);
    }
  };

  useLayoutEffect(() => {
    checkProducts();
  }, []);

  useEffect(() => {
    preselectZone();
  }, [zoneLocations]);

  const onZoneChange = (event: SelectChangeEvent<string>) => {
    setSelectedZone(event.target.value);
    zone.set(event.target.value);
  };

  const onDeviceChange = async (event: SelectChangeEvent<string>) => {
    const selectedCamera = cameraDevices.find(device => device.id === event.target.value)!;
    await setCamera(selectedCamera);
    await setDeviceIdToIndexDB(event.target.value);
  };

  useEffect(() => {
    setSaveDisabled(Boolean(!selectedZone || !selectedCameraDevice));
  }, [selectedZone, selectedCameraDevice]);

  useEffect(() => {
    initCameraDevices();
  }, []);

  return (
    <div className={classes.container} key={localStorage.getItem(FREYA_ORIENTATION_KEY)}>
      <div className={classes.title}>
        <QrCodeScannerIcon className={classes.titleIcon} />
        <div>{t("kioskPrintSettings.title")}</div>
      </div>
      <div className={classes.subtitle}>{t("kioskPrintSettings.subtitle")}</div>
      <Alert className={classes.alert} severity="warning">
        {t("kioskPrintSettings.alert")}
      </Alert>
      <div>
        <ScannerCheckbox />
        <FormLabel className={classes.inputsTitle}>{t("kioskPrintSettings.inputTitle")}</FormLabel>
        <Grid className={classes.selectGrid} container alignItems="flex-start" spacing={2} sx={{ mb: 4 }}>
          <FreyaPrintSettingsDeviceSelector
            cameraDevices={cameraDevices}
            selectedCameraDevice={selectedCameraDevice}
            getAvailableCameraDevices={initCameraDevices}
            onDeviceChange={onDeviceChange}
          />
          <Select
            renderIcon={() => <FormControl size="small" className={classes.select} />}
            label={t("kioskPrintSettings.zoneLocationSelect")}
            name={t("kioskPrintSettings.zoneLocationSelect")}
            value={selectedZone}
            onChange={onZoneChange}
            native={false}
          >
            {zoneLocations &&
              zoneLocations.map(zone => (
                <MenuItem key={zone.id} value={zone.id}>
                  {zone.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <OrientationToggle />
      </div>
    </div>
  );
};
